import * as React from 'react';
import { styled } from '@linaria/react';

import useMousePositionWithinElement from '@/hooks/use-mouse-position-within-element';

import Demo, { DemoSliderControl } from '@/components/Demo';
import ClientOnlySnippet from '@/components/CodeSnippet/ClientOnlySnippet';

function BackdropInitialDemo({}) {
  const tokyoRef = React.useRef<HTMLImageElement>(null);

  const [isTrackingMouse, setIsTrackingMouse] = React.useState(false);
  const [mousePosition] = useMousePositionWithinElement(tokyoRef, {
    buffer: 10,
    onEnterBox: () => setIsTrackingMouse(true),
    onLeaveBox: () => setIsTrackingMouse(false),
  });

  const hasMousePosition =
    typeof mousePosition.x === 'number' &&
    typeof mousePosition.y === 'number';

  const boxPosition = hasMousePosition
    ? {
        top: mousePosition.y,
        left: mousePosition.x,
      }
    : {
        top: '80%',
        left: '50%',
      };

  return (
    <Demo
      initialValues={{
        sepia: 50,
        brightness: 130,
      }}
      controlPosition="bottom"
      outputWrapper={null}
      controls={[
        <DemoSliderControl
          key="sepia"
          id="sepia"
          label="Sepia"
          min={0}
          max={100}
          previewRenderer={(val) => `${val}%`}
        />,
        <DemoSliderControl
          key="brightness"
          id="brightness"
          label="Brightness"
          min={50}
          max={300}
          previewRenderer={(val) => `${val}%`}
        />,
      ]}
    >
      {({ sepia, brightness }) => {
        return (
          <Wrapper>
            <ImageWrapper>
              <Tokyo
                ref={tokyoRef}
                src="/images/backdrop-filter/tokyo.jpg"
                loading="lazy"
              />
              <Cursor
                // @ts-ignore
                style={{
                  ...boxPosition,
                  '--scale': isTrackingMouse ? 1 : 2,
                  WebkitBackdropFilter: `sepia(${sepia}%) brightness(${brightness}%)`,
                  backdropFilter: `sepia(${sepia}%) brightness(${brightness}%)`,
                }}
              />
            </ImageWrapper>
            <CodeWrapper>
              <ClientOnlySnippet
                lockedToColorMode="dark"
                omitCopyButton
                lang="css"
                code={`\
.magic-ring {
  backdrop-filter: sepia(${sepia}%) brightness(${brightness}%);
}\
`}
              />
            </CodeWrapper>
          </Wrapper>
        );
      }}
    </Demo>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const ImageWrapper = styled.div`
  position: relative;
  overflow: clip;
  cursor: none;
`;

const Tokyo = styled.img`
  display: block;
  border-radius: 4px;
  aspect-ratio: 1400 / 877;
  touch-action: none;
`;

const Cursor = styled.div`
  position: absolute;
  width: 260px;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  border: 6px solid white;
  outline: 4px solid black;
  transform: translate(-50%, -50%) scale(var(--scale));
  transition: transform 500ms;
  will-change: transform;
  pointer-events: none;
  box-shadow:
    inset 0px 2px 4px hsl(0deg 0% 0% / 0.2),
    inset 0px 6px 9px hsl(0deg 0% 0% / 0.2),
    inset 0px 18px 26px hsl(0deg 0% 0% / 0.5);

  @media (max-width: 44rem) {
    width: 30%;
    aspect-ratio: 1 / 1;
  }
`;

const CodeWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 550px;
  min-height: 8rem;
  border: 1px solid var(--color-gray-200);
  background: var(--color-background);
  align-self: center;
  border-radius: 4px;
`;

export default BackdropInitialDemo;
